import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  IPatientFileSelectionDialogData,
  PatientFileSelectionDialogComponent,
} from '@principle-theorem/ng-media-manager';
import { OrganisationService } from '@principle-theorem/ng-principle-shared';
import { DialogPresets } from '@principle-theorem/ng-shared';
import {
  AccClaimFundingRequest,
  Patient,
  PatientForm,
  Practice,
} from '@principle-theorem/principle-core';
import {
  AccPatientFormType,
  FundingRequestStatus,
  IAccClaimFundingRequest,
  IAccClaimFundingRequestData,
  IAccDocumentMetadata,
  IMedia,
  IPatient,
  IPatientForm,
  isAccPatientForm,
  IStaffer,
  PatientFormStatus,
  PatientRelationshipType,
} from '@principle-theorem/principle-core/interfaces';
import {
  filterUndefined,
  Firestore,
  getError,
  multiFilter,
  snapshot,
  toNamedDocument,
  WithRef,
} from '@principle-theorem/shared';
import { combineLatest, Observable } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import {
  AccClaimLodgementFormDialogComponent,
  IAccClaimLodgementFormDialogData,
} from '../components/acc/acc-claim-lodgement-form-dialog/acc-claim-lodgement-form-dialog.component';
import {
  AccSendDocumentsDialogComponent,
  IAccSendDocumentsDialogData,
} from '../components/acc/acc-send-documents-dialog/acc-send-documents-dialog.component';
import {
  AccViewClaimDocumentsDialogComponent,
  IAccViewClaimDocumentsDialogData,
} from '../components/acc/acc-view-claim-documents-dialog/acc-view-claim-documents-dialog.component';
import { AccApiService } from './acc-api.service';

@Injectable()
export class AccClaimService {
  private _organisation = inject(OrganisationService);
  private _accApi = inject(AccApiService);
  private _dialog = inject(MatDialog);
  private _snackBar = inject(MatSnackBar);
  private _practice$ = this._organisation.practice$.pipe(filterUndefined());

  getAccClaimRequests$(
    patient$: Observable<WithRef<IPatient> | undefined>
  ): Observable<WithRef<IAccClaimFundingRequest>[]> {
    return combineLatest([this._practice$, patient$]).pipe(
      switchMap(([practice, patient]) =>
        patient
          ? Patient.withPatientRelationships$(
              patient,
              [PatientRelationshipType.DuplicatePatient],
              (relatedPatient) =>
                AccClaimFundingRequest.byPatient$(practice, relatedPatient)
            )
          : AccClaimFundingRequest.all$(practice)
      )
    );
  }

  async openPatientForm(
    form: WithRef<IPatientForm>,
    patient: WithRef<IPatient>
  ): Promise<void> {
    if (!isAccPatientForm(form)) {
      return;
    }

    const claimRef = form.associatedDocument;

    if (!claimRef) {
      return;
    }

    const claim = await Firestore.getDoc(claimRef);
    await this.viewClaimLodgement(patient, claim, form.formType);
  }

  async viewClaimLodgement(
    patient: WithRef<IPatient>,
    existingClaim?: WithRef<IAccClaimFundingRequest>,
    accPatientFormType?: AccPatientFormType
  ): Promise<void> {
    const practice = await snapshot(this._practice$);
    const testMode = this._accApi.testModeEnabled();
    const claim = existingClaim ?? (await this.saveClaim(patient));

    const savedClaim = await this._dialog
      .open<
        AccClaimLodgementFormDialogComponent,
        IAccClaimLodgementFormDialogData,
        WithRef<IAccClaimFundingRequest>
      >(
        AccClaimLodgementFormDialogComponent,
        DialogPresets.extraLarge({
          data: {
            practice,
            testMode,
            patient,
            claim,
            accPatientFormType,
          },
        })
      )
      .afterClosed()
      .toPromise();

    const alreadySubmitted =
      savedClaim && !AccClaimFundingRequest.isClaimEditable(savedClaim);
    if (!savedClaim || alreadySubmitted) {
      return;
    }

    try {
      const submittedClaimRef = await this._accApi.submitClaim(savedClaim);
      if (!submittedClaimRef) {
        return;
      }
      await AccClaimFundingRequest.updateIssuedPatientForms(
        patient,
        savedClaim
      );
      this._snackBar.open('Claim submitted successfully');
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error submitting claim:', getError(error));
      this._snackBar.open('Error submitting claim');
    }
  }

  async saveClaim(
    patient: WithRef<IPatient>,
    fundingRequestData?: IAccClaimFundingRequestData,
    claim?: WithRef<IAccClaimFundingRequest>
  ): Promise<WithRef<IAccClaimFundingRequest>> {
    try {
      const practice = await snapshot(this._practice$);

      if (claim) {
        const data = { ...claim.data, ...fundingRequestData };
        const updatedClaim = AccClaimFundingRequest.resetFailedToDraft({
          ...claim,
          data,
        });
        await Firestore.saveDoc(updatedClaim);
        return updatedClaim;
      }

      const fundingRequest = AccClaimFundingRequest.init({
        data: {
          claimData: {},
          metadata: {},
          ...fundingRequestData,
        },
        status: FundingRequestStatus.Draft,
        patient: toNamedDocument(patient),
      });

      const claimRef = await Firestore.addDoc<IAccClaimFundingRequest>(
        Practice.fundingRequestCol(practice),
        fundingRequest
      );

      return await Firestore.getDoc(claimRef);
    } catch (error) {
      this._snackBar.open('Error saving claim');
      throw new Error(`Failed to save claim: ${getError(error)}`);
    }
  }

  async addDocuments(
    claimRequest: WithRef<IAccClaimFundingRequest>
  ): Promise<void> {
    const patient = await Firestore.getDoc(claimRequest.patient.ref);

    const metadata = await this._dialog
      .open<
        AccSendDocumentsDialogComponent,
        IAccSendDocumentsDialogData,
        IAccDocumentMetadata
      >(
        AccSendDocumentsDialogComponent,
        DialogPresets.medium({ data: { claimRequest, patient } })
      )
      .afterClosed()
      .toPromise();
    if (!metadata) {
      return;
    }

    const selectedMedia = await this._dialog
      .open<
        PatientFileSelectionDialogComponent,
        IPatientFileSelectionDialogData,
        WithRef<IMedia>[]
      >(
        PatientFileSelectionDialogComponent,
        DialogPresets.extraLarge({
          data: { patient },
        })
      )
      .afterClosed()
      .toPromise();
    if (!selectedMedia || !selectedMedia.length) {
      return;
    }

    const mediaPaths = selectedMedia.map((mediaItem) => mediaItem.path);

    this._snackBar.open('Sending Documents to ACC');
    const response = await this._accApi.sendDocument(
      mediaPaths,
      metadata,
      claimRequest.ref
    );
    this._snackBar.open(
      response ? 'Documents Succesfully Sent' : 'Error Sending Documents'
    );
  }

  viewDocuments(claimRequest: WithRef<IAccClaimFundingRequest>): void {
    this._dialog.open<
      AccViewClaimDocumentsDialogComponent,
      IAccViewClaimDocumentsDialogData
    >(
      AccViewClaimDocumentsDialogComponent,
      DialogPresets.extraLarge({ data: { claimRequest } })
    );
  }

  async deleteClaim(
    claimRequest: WithRef<IAccClaimFundingRequest>
  ): Promise<void> {
    await Firestore.deleteDoc(claimRequest.ref);
  }

  async issuePatientForms(
    claim: WithRef<IAccClaimFundingRequest>,
    patient: WithRef<IPatient>,
    staffer: WithRef<IStaffer>,
    selectedForms: AccPatientFormType[]
  ): Promise<void> {
    try {
      const alreadyIssuedForms = await snapshot(
        PatientForm.byAssociatedDocument$(
          patient,
          claim.ref,
          selectedForms
        ).pipe(multiFilter((form) => form.status === PatientFormStatus.Issued))
      );

      const formsToIssue = selectedForms.filter(
        (form) =>
          !alreadyIssuedForms.some((issuedForm) => issuedForm.formType === form)
      );

      if (!formsToIssue.length) {
        return;
      }

      await AccClaimFundingRequest.issuePatientForms(
        patient,
        claim,
        staffer,
        formsToIssue
      );

      this._snackBar.open('ACC forms issued to patient');
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error issuing patient forms:', getError(error));
      this._snackBar.open('Error issuing patient forms');
    }
  }
}
